import type { PerspectiveCamera } from 'three';
import type { MouseLook } from '../core/MouseLook';
import { type Settings, saveSettings } from '../core/settings';

/**
 * Player-facing settings on the start screen: sensitivity and FOV sliders,
 * persisted via the versioned Settings store and applied live. (The T tuning
 * panel remains the dev-only tool for movement constants.)
 */
export function buildSettingsPanel(
  parent: HTMLElement,
  settings: Settings,
  look: MouseLook,
  camera: PerspectiveCamera,
  onVolume: (volume: number) => void,
  onQuality: (quality: Settings['quality']) => void,
): void {
  const dropdown = (
    label: string,
    options: readonly string[],
    initial: string,
    onChange: (value: string) => void,
  ): void => {
    const row = document.createElement('label');
    row.className = 'settings-row';
    const name = document.createElement('span');
    name.textContent = label;
    const select = document.createElement('select');
    select.className = 'settings-select';
    for (const opt of options) {
      const o = document.createElement('option');
      o.value = opt;
      o.textContent = opt;
      o.selected = opt === initial;
      select.appendChild(o);
    }
    select.addEventListener('change', () => onChange(select.value));
    row.append(name, select);
    parent.appendChild(row);
  };

  const slider = (
    label: string,
    min: number,
    max: number,
    step: number,
    initial: number,
    format: (v: number) => string,
    onChange: (v: number) => void,
  ): void => {
    const row = document.createElement('label');
    row.className = 'settings-row';
    const name = document.createElement('span');
    name.textContent = label;
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(initial);
    const value = document.createElement('span');
    value.className = 'settings-value';
    value.textContent = format(initial);
    input.addEventListener('input', () => {
      const v = Number(input.value);
      value.textContent = format(v);
      onChange(v);
    });
    row.append(name, input, value);
    parent.appendChild(row);
  };

  slider(
    'sensitivity',
    0.0006,
    0.008,
    0.0002,
    settings.sensitivity,
    (v) => v.toFixed(4),
    (v) => {
      settings.sensitivity = v;
      look.sensitivity = v;
      saveSettings(settings);
    },
  );

  slider(
    'field of view',
    60,
    130,
    1,
    settings.fov,
    (v) => `${v}°`,
    (v) => {
      settings.fov = v;
      camera.fov = v;
      camera.updateProjectionMatrix();
      saveSettings(settings);
    },
  );

  slider(
    'volume',
    0,
    1,
    0.05,
    settings.volume,
    (v) => `${Math.round(v * 100)}%`,
    (v) => {
      settings.volume = v;
      onVolume(v);
      saveSettings(settings);
    },
  );

  dropdown('graphics', ['low', 'medium', 'high', 'ultra'], settings.quality, (v) => {
    const q = v as Settings['quality'];
    settings.quality = q;
    onQuality(q);
    saveSettings(settings);
  });

  dropdown('auto-bhop', ['on', 'off'], settings.autoBhop ? 'on' : 'off', (v) => {
    settings.autoBhop = v === 'on';
    saveSettings(settings);
  });
}
